import React, { useCallback, useEffect, useState } from 'react';
import { cns } from '../../utils';
import css from './Lightbox.module.scss';
import ArrowLeft from '../../assets/chevron-left.svg';
import ArrowRight from '../../assets/chevron-right.svg';
import { Document } from '../../../interfaces/customer-api';
import { Spinner } from '../Spinner/Spinner';

interface LightboxProps {
    pics: Document[];
    initialIndex: number;
    onClose: () => void;
}

export const Lightbox: React.FC<LightboxProps> = ({ pics, initialIndex, onClose }) => {
    const [index, setIndex] = useState(initialIndex);
    const [isLoaded, setIsLoaded] = useState(false);
    const current = pics[index];
    const hasMultiplePics = pics.length > 1;

    const navigate = (nextIndex: number) => {
        setIsLoaded(false);
        setIndex(nextIndex);
    };

    const prev = useCallback(() => {
        setIsLoaded(false);
        setIndex((currentIndex) => (currentIndex - 1 + pics.length) % pics.length);
    }, [pics.length]);

    const next = useCallback(() => {
        setIsLoaded(false);
        setIndex((currentIndex) => (currentIndex + 1) % pics.length);
    }, [pics.length]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                prev();
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                next();
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        };

        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [next, prev, onClose]);

    return (
        <div className={css.overlay} onClick={onClose}>
            <div className={css.content}>
                {hasMultiplePics && (
                    <button className={css.nav} onClick={(e) => { e.stopPropagation(); prev(); }}>
                        <ArrowLeft />
                    </button>
                )}
                <div className={css.imageWrap} onClick={(e) => e.stopPropagation()}>
                    {!isLoaded && (
                        <div className={css.imageSpinner}>
                            <Spinner size={3} />
                        </div>
                    )}
                    <img
                        src={current.url}
                        alt={current.name}
                        className={css.image}
                        style={{ visibility: isLoaded ? 'visible' : 'hidden' }}
                        onLoad={() => setIsLoaded(true)}
                    />
                    {hasMultiplePics && (
                        <div className={css.dots}>
                            {pics.map((pic, i) => (
                                <button
                                    key={pic.key}
                                    className={cns(css.dot, i === index && css.dotActive)}
                                    onClick={() => navigate(i)}
                                />
                            ))}
                        </div>
                    )}
                </div>
                {hasMultiplePics && (
                    <button className={css.nav} onClick={(e) => { e.stopPropagation(); next(); }}>
                        <ArrowRight />
                    </button>
                )}
            </div>
        </div>
    );
};
